import { byStartsAt, seasonProgress, type Rsvp, type SeasonPublic, type SquadPublic } from '@otryad/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useHaptic } from '@/app/TmaProvider';
import { GroupHelp } from '@/components/GroupHelp';
import { HoldNotice } from '@/components/HoldNotice';
import { RsvpButtons } from '@/components/RsvpButtons';
import { TabBar } from '@/components/TabBar';
import { Button, Card, Countdown, EmptyState, ErrorState, ListRow, Screen, Skeleton, StatusBadge } from '@/components/ui';
import { useAppFeatures } from '@/lib/features';
import { formatDateTime, formatTime } from '@/lib/format';
import { unpaidNote, unpaidText } from '@/lib/guarantee';
import { apiReason, openTelegram } from '@/lib/links';
import { relationLabel } from '@/lib/myLabels';
import { errorText, fmt, texts, weekdayLong } from '@/lib/texts';
import { paymentTone, seasonTone } from '@/lib/tones';
import { trpc } from '@/lib/trpc';

const t = texts.home;

/** Строка «Твоих сборов»: сбор своего отряда или запись в чужом (разовое место, замена, пробный). */
interface UpcomingRow {
  id: string;
  index: number;
  startsAt: string;
  timezone: string;
  subtitle: string;
  rsvp: Rsvp | null;
}

/** «суббота» — день недели сбора в таймзоне города. */
function weekdayOf(startsAt: string, timezone: string): string {
  return new Intl.DateTimeFormat('ru-RU', { timeZone: timezone, weekday: 'long' }).format(
    new Date(startsAt),
  );
}

/** Карточка ближайшего сбора во весь экран: активность, дата, площадка, отсчёт, RSVP (BLUEPRINT 8). */
function NextGatheringCard({
  next,
  onAnswer,
  pending,
  error,
}: {
  next: NonNullable<SquadPublic['nextGathering']>;
  onAnswer: (rsvp: Rsvp) => void;
  pending: boolean;
  error: string | null;
}) {
  const navigate = useNavigate();
  const canAnswer = Date.now() < new Date(next.rsvpLockAt).getTime();
  return (
    <Card accent>
      {/* Внутрь сбора ведёт сама шапка карточки, а не только кнопка внизу. Ответы RSVP оставлены
          вне цели: промах по «Иду» не должен уводить с экрана. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => void navigate(`/gathering/${next.id}`)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            void navigate(`/gathering/${next.id}`);
          }
        }}
        className="pressable -mx-2 flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="text-muted text-xs font-semibold tracking-wide uppercase">
            {next.isExtra ? texts.schedule.extra : fmt(texts.schedule.gatheringN, { n: next.index })} · {next.activity.name}
          </div>
          <div className="num mt-1 text-[26px] leading-tight font-bold tracking-tight">
            {formatDateTime(next.startsAt, next.timezone)}
          </div>
          <div className="text-muted mt-1 text-sm">
            {next.venue ? `${next.venue.name} · ${next.venue.address}` : texts.gathering.noVenue}
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-muted text-sm">{t.startsIn}</span>
            <Countdown to={next.startsAt} className="text-xl" />
          </div>
        </div>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          className="text-muted mt-1 shrink-0"
        >
          <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>

      <div className="mt-4">
        <div className="mb-2 text-base font-semibold">{texts.rsvp.question}</div>
        <RsvpButtons
          value={next.myRsvp}
          onChange={onAnswer}
          disabled={!canAnswer}
          loading={pending}
        />
        <p className="text-muted mt-2 text-xs">
          {canAnswer
            ? fmt(texts.rsvp.changeUntil, { when: formatDateTime(next.rsvpLockAt, next.timezone) })
            : texts.rsvp.locked}
        </p>
        <p className="text-muted text-xs">{fmt(texts.rsvp.counts, next.rsvpCounts)}</p>
        {error ? (
          <p className="text-danger mt-1 text-sm" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <Button
        variant="secondary"
        fullWidth
        className="mt-4"
        onClick={() => void navigate(`/gathering/${next.id}`)}
      >
        {t.details}
      </Button>
    </Card>
  );
}

function SeasonCard({ season, timezone }: { season: SeasonPublic; timezone: string }) {
  const navigate = useNavigate();
  const unpaid = unpaidNote(season);
  // Раунд персон 1, C3: сезон по шаблону без отменённых, доп. сборы отдельно — не «0 из 8»
  const progress = seasonProgress(season.gatherings);
  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  return (
    <Card
      title={t.seasonTitle}
      action={
        <Button variant="ghost" onClick={() => void navigate('/season')}>
          {t.seasonOpen}
        </Button>
      }
    >
      {/* Прогресс сезона ведёт в расписание: раньше это делала только кнопка в шапке карточки. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => void navigate('/season')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            void navigate('/season');
          }
        }}
        className="pressable -mx-2 cursor-pointer rounded-lg px-2 py-1"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="text-lg font-bold tracking-tight">
            {fmt(progress.extra > 0 ? t.seasonProgressExtra : t.seasonProgress, {
              number: season.number,
              done: progress.done,
              total: progress.total,
              extra: progress.extra,
            })}
          </div>
          <StatusBadge tone={seasonTone(season.status)}>
            {texts.season.status[season.status]}
          </StatusBadge>
        </div>
        <div className="bg-surface-2 mt-3 h-2 w-full overflow-hidden rounded-full" aria-hidden="true">
          <div className="bg-accent h-full rounded-full" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {season.status !== 'FINISHED' && season.status !== 'CANCELLED' ? (
        <div className="mt-4">
          {season.myPayment === 'NONE' || season.myPayment === 'REFUNDED' ? (
            <>
              <div className="font-semibold">{t.payTitle}</div>
              {/* Раунд 4, M3: срок и последствие — по логике дедлайна, а не «не гарантируется» */}
              {unpaid ? (
                <p className="text-muted mb-3 text-sm">{unpaidText(unpaid, timezone)}</p>
              ) : null}
              <Button fullWidth onClick={() => void navigate(`/pay/season/${season.id}`)}>
                {t.payCta}
              </Button>
            </>
          ) : (
            <>
              <StatusBadge tone={paymentTone(season.myPayment)}>
                {season.myPayment === 'PAID' ? t.paid : t.payPending}
              </StatusBadge>
              {/* Счёт открыт, но не оплачен: карта не прошла, окно закрылось. Экран оплаты
                  умеет открыть счёт заново — без этой кнопки туда просто нельзя вернуться. */}
              {season.myPayment === 'PENDING' ? (
                <>
                  <p className="text-muted mt-2 text-sm">{t.payPendingHint}</p>
                  {unpaid ? (
                    <p className="text-muted mt-1 text-sm">{unpaidText(unpaid, timezone)}</p>
                  ) : null}
                  <Button
                    fullWidth
                    className="mt-3"
                    onClick={() => void navigate(`/pay/season/${season.id}`)}
                  >
                    {t.payContinue}
                  </Button>
                </>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </Card>
  );
}

/** `/home` — «приложение как расписание»: ближайший сбор, сезон, группа (Stage 6, задача 1). */
export function HomeScreen() {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const utils = trpc.useUtils();
  const squad = trpc.squad.my.useQuery({});
  const season = trpc.season.current.useQuery({});
  const rsvp = trpc.gathering.rsvp.useMutation();
  // Раунд персон 1, M6/C8: разовое место, замена и пробный — тоже «Твои сборы»
  const { myGatherings } = useAppFeatures();
  const mine = trpc.my.gatherings.useQuery(undefined, { enabled: myGatherings });
  const [rsvpError, setRsvpError] = useState<string | null>(null);
  // Занято до обновления данных, а не до ответа мутации: иначе между ними мелькает старый ответ
  const [answering, setAnswering] = useState(false);

  const answer = async (gatheringId: string, value: Rsvp) => {
    setRsvpError(null);
    setAnswering(true);
    haptic.impact('medium');
    try {
      await rsvp.mutateAsync({ gatheringId, rsvp: value });
      haptic.notify('success');
      await Promise.all([utils.squad.invalidate(), utils.season.invalidate(), utils.gathering.invalidate()]);
    } catch (e) {
      haptic.notify('error');
      setRsvpError(errorText(apiReason(e)));
    } finally {
      setAnswering(false);
    }
  };

  if (squad.isError) {
    return (
      <Screen title={t.title} footer={<TabBar active="home" />}>
        <ErrorState onRetry={() => void squad.refetch()} />
      </Screen>
    );
  }
  if (squad.isPending) {
    return (
      <Screen title={t.title}>
        <Skeleton className="h-64" />
        <Skeleton className="h-28" />
      </Screen>
    );
  }
  const s = squad.data;
  if (!s) {
    return (
      <Screen title={t.title}>
        <EmptyState
          title={t.noSquadTitle}
          body={t.noSquadBody}
          actionLabel={t.toPool}
          onAction={() => void navigate('/pool')}
        />
      </Screen>
    );
  }

  const se = season.data ?? null;
  const next = s.nextGathering;
  // Свои ближайшие сборы: расписание сезона без того, что уже показано карточкой сверху, и записи
  // в чужих отрядах из «Моих сборов» — по времени начала, а не по номеру (раунд персон 1, C2, M6):
  // доп. сбор №8 во вторник раньше №3 в четверг, разовое место — между ними.
  const squadRows: UpcomingRow[] = (se?.gatherings ?? [])
    .filter(
      (g) =>
        g.id !== next?.id &&
        (g.status === 'PLANNED' || g.status === 'CONFIRMED') &&
        new Date(g.startsAt).getTime() > Date.now(),
    )
    .map((g) => ({
      id: g.id,
      index: g.index,
      startsAt: g.startsAt,
      timezone: g.timezone,
      subtitle: [
        g.activity.name,
        g.isExtra ? texts.schedule.extra : null,
        g.venue?.name ?? null,
      ]
        .filter(Boolean)
        .join(' · '),
      rsvp: g.myRsvp,
    }));
  const guestRows: UpcomingRow[] = (mine.data?.items ?? [])
    .filter((i) => i.relation !== 'SQUAD')
    .map((i) => ({
      id: i.gatheringId,
      index: 0,
      startsAt: i.startsAt,
      timezone: i.timezone,
      subtitle: [i.activity.name, relationLabel(i), i.venueName].filter(Boolean).join(' · '),
      rsvp: null,
    }));
  const upcoming = byStartsAt([...squadRows, ...guestRows]).slice(0, 3);
  // Раунд персон 1, M6: шапка «Ближайший сбор» говорит о карточке под ней, а не о вечере отряда —
  // ближайшим бывает доп. сбор в субботу днём. Вечер отряда — когда сбора впереди нет.
  const subtitle = next
    ? `${next.activity.name} · ${weekdayOf(next.startsAt, next.timezone)}, ${formatTime(next.startsAt, next.timezone)}`
    : `${s.primaryActivity.name} · ${weekdayLong(s.weekday).toLowerCase()}, ${s.startTime}`;

  return (
    <Screen title={t.title} subtitle={subtitle} footer={<TabBar active="home" />}>
      {/* Раунд персон 3 (M4): бронь, которая ждёт оплаты, — на главном, а не только в «Моих сборах» */}
      <HoldNotice />

      {s.status === 'PAUSED' ? (
        <Card>
          <EmptyState title={t.pausedTitle} body={t.pausedBody} />
        </Card>
      ) : null}

      {next ? (
        <NextGatheringCard
          next={next}
          onAnswer={(v) => void answer(next.id, v)}
          pending={answering}
          error={rsvpError}
        />
      ) : season.isPending ? (
        <Skeleton className="h-40" />
      ) : !se ? (
        <Card>
          <EmptyState title={t.noSeasonTitle} body={t.noSeasonBody} />
        </Card>
      ) : se.status === 'FINISHED' ? (
        <Card>
          <EmptyState
            title={t.finishedTitle}
            body={t.finishedBody}
            actionLabel={t.seasonOpen}
            onAction={() => void navigate('/season')}
          />
        </Card>
      ) : (
        <Card>
          <EmptyState
            title={t.noUpcomingTitle}
            body={t.noUpcomingBody}
            actionLabel={t.seasonOpen}
            onAction={() => void navigate('/season')}
          />
        </Card>
      )}

      {se ? <SeasonCard season={se} timezone={s.timezone} /> : null}

      {/* «Твои сборы» — то, куда человек уже записан; вход в новое стоит ниже ближайшего сбора
          намеренно: приложение открывают с вопросом «когда» и «я иду», а не «куда бы ещё». */}
      <Card title={t.mineTitle} flush>
        {upcoming.length > 0 ? (
          upcoming.map((g) => (
            <ListRow
              key={g.id}
              title={formatDateTime(g.startsAt, g.timezone)}
              subtitle={g.subtitle}
              trailing={
                g.rsvp === 'YES' ? (
                  <StatusBadge tone="success">{texts.rsvp.yes}</StatusBadge>
                ) : g.rsvp === 'NO' ? (
                  <StatusBadge>{texts.rsvp.no}</StatusBadge>
                ) : undefined
              }
              onClick={() => void navigate(`/gathering/${g.id}`)}
            />
          ))
        ) : (
          <p className="text-muted px-4 pb-3 text-sm">{t.noUpcomingBody}</p>
        )}
        <ListRow
          title={t.mineMore}
          subtitle={t.mineMoreHint}
          onClick={() => void navigate('/open-seats')}
        />
      </Card>

      <Card title={t.group}>
        <p className="text-muted mb-3 text-sm">{t.groupBody}</p>
        {s.tgInviteLink ? (
          <Button variant="secondary" fullWidth onClick={() => openTelegram(s.tgInviteLink!)}>
            {t.groupOpen}
          </Button>
        ) : (
          <GroupHelp squadId={s.id} isCaptain={s.me.isCaptain} hasGroup={s.hasGroup} secondary />
        )}
      </Card>
    </Screen>
  );
}
